import { useUpdateURLSearchParams } from '@/hooks/useConvertToURLSearchParams';
import type {
  AdminInvoiceReportsSearchQuery,
  FetchAdminInvoiceReportsArgs,
} from '@/features/admin_invoice_reports/types';
import { buildOrderByArray } from '@/features/admin_invoice_reports/utils/buildOrderByArray';
import { useCallback, useMemo, useState } from 'react';
import type { MultiColumnSortState } from '../Table/types';

type UseHandleSearchQueryResult = {
  fetchArgs: FetchAdminInvoiceReportsArgs;
  searchQuery: AdminInvoiceReportsSearchQuery;
  sortOrders: MultiColumnSortState[];
  onSearch: (query: AdminInvoiceReportsSearchQuery) => void;
  onMultiColumnSort: (sortStates: MultiColumnSortState[]) => void;
};

export const useAdminInvoiceReportsHandleSearchQuery = (
  initialQuery: AdminInvoiceReportsSearchQuery = {}
): UseHandleSearchQueryResult => {
  const [searchQuery, setSearchQuery] =
    useState<AdminInvoiceReportsSearchQuery>(initialQuery);
  const [sortOrders, setSortOrders] = useState<MultiColumnSortState[]>([]);

  const updateSearchParams = useUpdateURLSearchParams();

  const onSearch = useCallback(
    (query: AdminInvoiceReportsSearchQuery) => {
      setSearchQuery(query);
      updateSearchParams(
        Object.fromEntries(
          Object.entries(query).filter(([, v]) => v !== undefined)
        )
      );
    },
    [updateSearchParams]
  );

  const onMultiColumnSort = useCallback(
    (sortStates: MultiColumnSortState[]) => {
      setSortOrders(sortStates);
    },
    []
  );

  const orderBy = useMemo(() => buildOrderByArray(sortOrders), [sortOrders]);

  const fetchArgs: FetchAdminInvoiceReportsArgs = useMemo(
    () => ({
      page: searchQuery.page ?? 1,
      perPage: 50,
      query: searchQuery,
      order_by: orderBy,
    }),
    [searchQuery, orderBy]
  );

  return { fetchArgs, searchQuery, sortOrders, onSearch, onMultiColumnSort };
};
